"use client";

import { resolvePremiumInviteFreeTelegramUrl } from "@/lib/premiumInviteFreeTelegramUrl";
import {
  fetchPremiumInvitePublicConfig,
  type PremiumInvitePublicConfig,
  PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY,
} from "@/lib/premiumInvitePublicConfig";
import { useEffect, useMemo, useState } from "react";

/**
 * URL Telegram gratis alineada con admin (`GET /api/premium-invite-config`) + env.
 * Devuelve también `publicConfig` para toggles Mux / copy friendly en `BioTemplatePremiumSimple`.
 * Solo hace fetch cuando `enabled` (p. ej. template premium simple en home o invite).
 * Tras guardar en admin: recargá la página o cambiá de pestaña y volvé — al volver a ser visible se vuelve a pedir la config (sin caché).
 *
 * @param countryCode ISO2 opcional — usa `free_telegram_url_by_country` cuando hay override.
 */
export function usePremiumInviteFreeTelegramPublicUrl(
  enabled: boolean,
  countryCode?: string | null,
): {
  url: string | undefined;
  publicConfig: PremiumInvitePublicConfig | null;
  ready: boolean;
} {
  const [publicConfig, setPublicConfig] = useState<PremiumInvitePublicConfig | null>(null);
  const [remoteReady, setRemoteReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setPublicConfig(null);
      setRemoteReady(true);
      return;
    }
    let cancelled = false;
    const run = async (soft: boolean) => {
      if (!soft) setRemoteReady(false);
      try {
        const cfg = await fetchPremiumInvitePublicConfig();
        if (!cancelled) setPublicConfig(cfg);
      } catch {
        if (!cancelled) setPublicConfig(null);
      } finally {
        if (!cancelled) setRemoteReady(true);
      }
    };
    void run(false);
    const onVisibility = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      void run(true);
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (cancelled || !e.persisted) return;
      void run(true);
    };
    const onStorage = (e: StorageEvent) => {
      if (cancelled || e.key !== PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY) return;
      void run(true);
    };
    /** Same-tab admin save (CustomEvent) — storage event only fires cross-tab. */
    const onLocalBroadcast = () => {
      if (!cancelled) void run(true);
    };
    // Backup only — admin save broadcasts CustomEvent + storage for instant soft-refetch.
    const poll = window.setInterval(() => {
      if (!cancelled) void run(true);
    }, 10_000);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("storage", onStorage);
    window.addEventListener(PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY, onLocalBroadcast);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY, onLocalBroadcast);
    };
  }, [enabled]);

  const url = useMemo(
    () =>
      resolvePremiumInviteFreeTelegramUrl(
        enabled ? publicConfig : null,
        countryCode,
      ),
    [enabled, publicConfig, countryCode],
  );

  return {
    url,
    publicConfig: enabled ? publicConfig : null,
    ready: !enabled || remoteReady,
  };
}
